import { useEffect, useRef, useState } from 'react';
import { PromotionSchedule } from '../../../../lib/api/promotionSchedule';
import { toDatePart } from '../utils';
import { shiftIsoByKstDays } from '../utils/kst';

/** 이 픽셀 이상 움직여야 드래그로 본다. 미만이면 클릭(모달 열기)으로 흘려보낸다. */
const DRAG_THRESHOLD_PX = 3;

export const DRAG_MODE = {
  /** 막대 전체 이동 — 기간 길이는 유지 */
  MOVE: 'move',
  /** 시작일만 변경 */
  RESIZE_START: 'resizeStart',
  /** 종료일만 변경 */
  RESIZE_END: 'resizeEnd',
} as const;

export type DragMode = (typeof DRAG_MODE)[keyof typeof DRAG_MODE];

/** 드래그 중 화면에 보여줄 임시 기간. 서버 왕복 동안에도 유지돼 낙관적 UX 를 만든다. */
export type BarPreview = {
  scheduleId: number;
  startDate: string;
  endDate: string;
};

type DragState = {
  schedule: PromotionSchedule;
  mode: DragMode;
  originX: number;
  hasMoved: boolean;
};

type UseBarDragParams = {
  /** 하루당 픽셀 폭 — 픽셀 이동량을 일 수로 환산하는 기준 */
  dayColumnWidth: number;
  /** 기간이 실제로 바뀐 경우에만 호출된다 */
  onCommit: (params: { schedule: PromotionSchedule; startDate: string; endDate: string }) => void;
  /** 임계값 미만으로 움직였을 때 (= 클릭) */
  onClick: (schedule: PromotionSchedule) => void;
};

type UseBarDragReturn = {
  preview: BarPreview | null;
  isDragging: boolean;
  /** 막대에 붙일 pointerdown 핸들러 팩토리 */
  startDrag: (schedule: PromotionSchedule, mode: DragMode) => (event: React.PointerEvent) => void;
  /** 커밋이 끝난 뒤(성공·실패 모두) preview 를 걷어낸다 */
  clearPreview: () => void;
  /**
   * 직전 pointer 상호작용을 이 훅이 처리했는지 확인하고 플래그를 소비한다.
   *
   * pointerup 뒤에는 브라우저가 click 도 발생시키므로, 막대의 onClick 이 그 click 을 그대로
   * 받으면 드래그 직후 모달이 열린다(폼 초기값은 드래그 이전 스냅샷이라 저장 시 기간이 되돌아간다).
   * 키보드 Enter·Space 는 pointer 이벤트 없이 click 만 만들어 이 플래그가 false 다.
   */
  consumePointerHandled: () => boolean;
};

/**
 * 일 단위로 옮기되 **시각은 보존한다** — 14시 시작 타임딜을 이틀 뒤로 끌면 여전히 14시 시작이어야 한다.
 * 날짜 덧셈은 KST 기준이라 접속 위치가 결과를 바꾸지 않는다 (`../utils/kst`).
 */
const shiftDate = (value: string, days: number): string => shiftIsoByKstDays(value, days);

/**
 * 간트 막대의 드래그 이동 / 좌우 리사이즈.
 *
 * 구글시트에서 병합 셀을 늘려 기간을 바꾸던 행위의 대체 동작이다.
 * HTML5 drag&drop 대신 pointer events 를 쓴 이유 — grid 위에서 픽셀 델타를 직접 다뤄야
 * "몇 일 옮겼는지"를 계산할 수 있고, 드래그 이미지·드롭존 개념이 필요 없다.
 */
export const useBarDrag = (params: UseBarDragParams): UseBarDragReturn => {
  const { dayColumnWidth, onCommit, onClick } = params;

  const [preview, setPreview] = useState<BarPreview | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const dragRef = useRef<DragState | null>(null);
  /**
   * 등록된 window listener 를 걷어내는 함수. 드래그 중 unmount 되면 pointerup 이 오지 않아
   * listener 와 dragRef 가 남고, 이후 pointer 이벤트가 낡은 상태로 setState 를 호출한다.
   */
  const teardownRef = useRef<(() => void) | null>(null);
  /** pointer 경로로 상호작용을 처리했다는 표시 — 뒤따라 오는 click 을 흘리기 위한 것 */
  const pointerHandledRef = useRef(false);

  useEffect(() => () => teardownRef.current?.(), []);

  const computeDates = (drag: DragState, dayDelta: number): { startDate: string; endDate: string } => {
    const { schedule, mode } = drag;

    if (mode === DRAG_MODE.MOVE) {
      return {
        startDate: shiftDate(schedule.startDate, dayDelta),
        endDate: shiftDate(schedule.endDate, dayDelta),
      };
    }

    if (mode === DRAG_MODE.RESIZE_START) {
      const nextStart = shiftDate(schedule.startDate, dayDelta);
      // 시작일이 종료일을 넘어가지 못하게 붙잡는다 (서버도 거부하지만 화면에서 먼저 막는다)
      return {
        // 날짜 기준으로 붙잡는다 (드래그는 일 단위라 시각까지 비교하면 어색하게 잠긴다)
        startDate: toDatePart(nextStart) > toDatePart(schedule.endDate) ? schedule.startDate : nextStart,
        endDate: schedule.endDate,
      };
    }

    const nextEnd = shiftDate(schedule.endDate, dayDelta);
    return {
      startDate: schedule.startDate,
      endDate: toDatePart(nextEnd) < toDatePart(schedule.startDate) ? schedule.endDate : nextEnd,
    };
  };

  const startDrag = (schedule: PromotionSchedule, mode: DragMode) => (event: React.PointerEvent) => {
    // 좌클릭만
    if (event.button !== 0) {
      return;
    }
    event.stopPropagation();
    event.preventDefault();

    /**
     * 진행 중인 드래그가 있으면 먼저 정리한다. 멀티터치로 두 번째 pointerdown 이 오면
     * window listener 두 세트가 공존하고 각 handleUp 이 공유 dragRef(나중에 누른 막대)를
     * 읽어 엉뚱한 기간이 저장된다. 덮어쓰인 teardown 은 unmount 정리에서도 빠진다.
     */
    teardownRef.current?.();

    dragRef.current = { schedule, mode, originX: event.clientX, hasMoved: false };
    setIsDragging(true);

    const handleMove = (moveEvent: PointerEvent) => {
      const drag = dragRef.current;
      if (!drag) {
        return;
      }

      const deltaX = moveEvent.clientX - drag.originX;
      if (!drag.hasMoved && Math.abs(deltaX) < DRAG_THRESHOLD_PX) {
        return;
      }
      drag.hasMoved = true;

      const dayDelta = Math.round(deltaX / dayColumnWidth);
      const dates = computeDates(drag, dayDelta);
      setPreview({ scheduleId: drag.schedule.id, ...dates });
    };

    const teardown = () => {
      window.removeEventListener('pointermove', handleMove);
      window.removeEventListener('pointerup', handleUp);
      window.removeEventListener('pointercancel', handleCancel);
      teardownRef.current = null;
      dragRef.current = null;
      setIsDragging(false);
    };

    /**
     * 브라우저가 포인터를 회수한 경우(터치 제스처 전환·창 전환 등). pointerup 이 오지 않으므로
     * 여기서 정리하고 **preview 는 폐기**한다 — 사용자가 놓은 지점이 확정된 게 아니다.
     *
     * ⚠️ 여기서는 `pointerHandledRef` 를 세우지 않는다. 취소된 포인터는 click 을 만들지 않아
     * 플래그가 소비되지 않고 남고, 그러면 **다음 막대 클릭이나 키보드 활성화가 대신 삼켜진다.**
     */
    const handleCancel = () => {
      teardown();
      setPreview(null);
    };

    const handleUp = (upEvent: PointerEvent) => {
      // teardown 이 dragRef 를 비우므로 먼저 스냅샷을 잡는다
      const drag = dragRef.current;
      teardown();
      pointerHandledRef.current = true;

      if (!drag) {
        return;
      }

      if (!drag.hasMoved) {
        setPreview(null);
        onClick(drag.schedule);
        return;
      }

      const dayDelta = Math.round((upEvent.clientX - drag.originX) / dayColumnWidth);
      const dates = computeDates(drag, dayDelta);

      // 하루도 안 옮겼으면 요청하지 않는다
      if (dates.startDate === drag.schedule.startDate && dates.endDate === drag.schedule.endDate) {
        setPreview(null);
        return;
      }

      // preview 는 그대로 두고 커밋 — 서버 응답을 기다리는 동안 막대가 원위치로 튀지 않게
      setPreview({ scheduleId: drag.schedule.id, ...dates });
      onCommit({ schedule: drag.schedule, ...dates });
    };

    window.addEventListener('pointermove', handleMove);
    window.addEventListener('pointerup', handleUp);
    window.addEventListener('pointercancel', handleCancel);
    teardownRef.current = teardown;
  };

  const consumePointerHandled = (): boolean => {
    const handled = pointerHandledRef.current;
    pointerHandledRef.current = false;

    return handled;
  };

  return {
    preview,
    isDragging,
    startDrag,
    clearPreview: () => setPreview(null),
    consumePointerHandled,
  };
};
